import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { useAsanaData } from 'Shared/utils/hooks';
import { createAsanaTask } from 'Shared/actions/todos';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import AsanaDots from 'Shared/images/AsanaDots.png';

import './CreateAsanaTaskModal.scss';

type Props = {
  itemUuid: string;
  isOpen: boolean;
  onToggle: () => void;
};

const CreateAsanaTaskModal = ({ itemUuid, isOpen, onToggle }: Props) => {
  const dispatch = useDispatch();
  const { tr } = useTranslation();
  const [project, setProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const asanaData = useAsanaData();
  const projects = asanaData?.projects;
  const optionsForSelect = projects ? projects.map(({ id, name }) => ({ value: id, label: name })) : [];

  useEffect(() => {
    (window as any).analytics.track('Opened Modal', { modal: 'Copy to Asana' });
  }, []);

  const handleSelectChange = value => {
    setProject(value);
    return null;
  };

  const handleCreateClick = () => {
    setSaving(true);
    dispatch(createAsanaTask({ itemUuid, projectId: project.value }))
      .then(() => WpToastManager.success(tr('perform.modals.asana.success_message')))
      .catch(() => WpToastManager.danger(tr('perform.modals.asana.error_message')))
      .finally(() => {
        onToggle();
        setSaving(false);
      });
  };

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={onToggle}>
      <WpModalHeader>
        <div>
          {tr('perform.modals.asana.title')}
          <img className="CreateAsanaTaskModal__logo ml-3" src={AsanaDots} alt="" />
        </div>
      </WpModalHeader>
      <WpModalBody>
        <div className="CreateAsanaTaskModal">
          <div>{tr('perform.modals.asana.description')}</div>
          <div className="small mb-2 mt-1 text-muted">{tr('perform.modals.asana.subtopics_note')}</div>

          <div className="CreateAsanaTaskModal__label">{tr('perform.modals.asana.projects_label')}</div>
          <Select
            isSearchable
            options={optionsForSelect}
            placeholder={tr('perform.modals.asana.project_placeholder')}
            value={project}
            onChange={handleSelectChange}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: '1050' }) }}
          />
          <div className="CreateAsanaTaskModal__spacer mt-1">
            {project && <div className="CreateAsanaTaskModal__note">{tr('perform.modals.asana.copy_note')}</div>}
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" disabled={saving} onClick={onToggle}>
            {tr('perform.common.cancel')}
          </WpButton>
          <WpButton disabled={saving || !project} onClick={handleCreateClick} loading={saving}>
            {tr('perform.modals.asana.create_task_button')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CreateAsanaTaskModal;
